import {
  ActionContext,
  ActionTree,
  GetterTree,
  Module,
  MutationTree
} from "vuex";
import { Notification } from "../../../../domain/entities/notifications/notification";
import notificationUseCases from "../../../../domain/usecase/impl/notification/notificationUseCases";
import { BusinessRuleException } from "../../../../domain/exceptions/exceptions";
import { RootState } from "../store";
class NotificationState {
  notifications: Notification[] = [];
  notification: Notification = new Notification();
  hasNewNotifications = false;
  newNotificationsCount = 0;
}

const state = new NotificationState();

const mutations: MutationTree<NotificationState> = {
  SET_NOTIFICATIONS(
    state: NotificationState,
    notifications: Notification[]
  ): void {
    state.notifications = notifications;

    if (state.notifications) {
      const unread = state.notifications.filter(
        (notification: Notification) => !notification.isRead
      );
      state.hasNewNotifications = unread.length > 0;
      state.newNotificationsCount = unread.length;
    }
  },
  SET_NOTIFICATION(state: NotificationState, notification: Notification): void {
    state.notification = notification;
  },
  SET_NEW_NOTIFICATIONS_INDICATOR(
    state: NotificationState,
    hasNewNotifications: boolean
  ): void {
    state.hasNewNotifications = hasNewNotifications;
  },
  MARK_AS_READ(state: NotificationState, notificationID: number): void {
    const index = state.notifications.findIndex(
      notification => notification.id === notificationID
    );
    if (index !== -1) {
      state.notifications.splice(index, 1, {
        ...state.notifications[index],
        isRead: true
      });
    }

    const unread = state.notifications.filter(
      notification => !notification.isRead
    );
    state.hasNewNotifications = unread.length > 0;
    state.newNotificationsCount = unread.length;
  },
  DELETE_NOTIFICATION(state: NotificationState, notificationID: number): void {
    state.notifications = state.notifications.filter(
      notification => notification.id !== notificationID
    );

    const unread = state.notifications.filter(
      notification => !notification.isRead
    );
    state.hasNewNotifications = unread.length > 0;
    state.newNotificationsCount = unread.length;
  }
};

const actions: ActionTree<NotificationState, NotificationState> = {
  async list({
    commit
  }: ActionContext<NotificationState, NotificationState>): Promise<void> {
    try {
      const response: Notification[] = await notificationUseCases.list();
      commit("SET_NOTIFICATIONS", response);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async watch(
    { commit }: ActionContext<NotificationState, NotificationState>,
    notificationID: number
  ): Promise<void> {
    try {
      await notificationUseCases.watch(notificationID);
      commit("MARK_AS_READ", notificationID);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async delete(
    { commit }: ActionContext<NotificationState, NotificationState>,
    notificationID: number
  ): Promise<void> {
    try {
      await notificationUseCases.delete(notificationID);
      commit("DELETE_NOTIFICATION", notificationID);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async get(
    { commit }: ActionContext<NotificationState, RootState>,
    notificationID: number
  ): Promise<void> {
    try {
      const response: Notification = await notificationUseCases.get(
        notificationID
      );
      commit("SET_NOTIFICATION", response);
    } catch (e) {
      if (e instanceof BusinessRuleException) {
        commit("SET_FORM_EXCEPTIONS", e);
        throw e;
      }
    }
  }
};

const getters: GetterTree<NotificationState, NotificationState> = {
  notifications: (state: NotificationState): Notification[] =>
    state.notifications,
  notification: (state: NotificationState): Notification => state.notification,
  hasNewNotifications: (state: NotificationState) => state.hasNewNotifications,
  newNotificationsCount: (state: NotificationState) =>
    state.newNotificationsCount
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} as Module<NotificationState, NotificationState>;
